import React from 'react';
import { Link } from 'wouter';
import { Tool } from '../../types';
import { ArrowRight } from 'lucide-react';

interface RelatedToolsProps {
  currentTool: Tool;
  allTools: Tool[];
}

export function RelatedTools({ currentTool, allTools }: RelatedToolsProps) {
  // Find related tools based on category, pricing, and features
  const relatedTools = allTools
    .filter(tool =>
      tool.id !== currentTool.id &&
      (tool.category === currentTool.category ||
       tool.pricing.some(p => currentTool.pricing.includes(p)) ||
       tool.features.some(f => currentTool.features.includes(f)))
    )
    .slice(0, 3);

  if (relatedTools.length === 0) return null;

  return (
    <section className="mt-16 border-t border-gray-200 pt-12">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Tools</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {relatedTools.map(tool => (
          <Link
            key={tool.id}
            href={`/tools/${tool.id}`}
            className="group block bg-white rounded-lg border border-gray-200 p-6 hover:border-blue-500 hover:shadow-lg transition-all"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                <img
                  src={`https://logo.clearbit.com/${tool.website?.replace(/^https?:\/\//, '').split('/')[0]}`}
                  alt={`${tool.name} logo`}
                  className="w-8 h-8 object-contain"
                  onError={(e) => {
                    e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(tool.name)}&background=f3f4f6&color=4b5563`;
                  }}
                />
              </div>
              <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
              {tool.name}
            </h3>
            <p className="text-sm text-gray-600 line-clamp-2 mb-3">
              {tool.description}
            </p>
            <span className="inline-block text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">
              {tool.category}
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}
